import { useEffect, useState, type FormEvent } from "react";
import { supabase } from "../../lib/supabase.js";
import { useAuth } from "../../lib/auth.js";
import { timeZoneLabel } from "../../lib/timeZone.js";
import { Button, Field, Input } from "../../lib/ui.js";
import { SectionCard, errorStyle } from "./common.js";

/** The business's address. The receptionist can tell a caller where you are; the state also decides the area
 *  code for call-route numbers entered without one. The state is shown but not editable here: the platform admin sets it, and it
 *  decides the business's time zone (every booking and opening hour follows it). */
export function AddressSection() {
  const { activeBusinessId } = useAuth();
  const [addressLine, setAddressLine] = useState("");
  const [suburb, setSuburb] = useState("");
  const [state, setState] = useState("");
  const [timeZone, setTimeZone] = useState("");
  const [postcode, setPostcode] = useState("");
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!activeBusinessId) return;
    void supabase
      .from("businesses")
      .select("address_line, suburb, state, timezone, postcode")
      .eq("id", activeBusinessId)
      .single()
      .then(({ data }) => {
        setAddressLine(data?.address_line ?? "");
        setSuburb(data?.suburb ?? "");
        setState(data?.state ?? "");
        setTimeZone(data?.timezone ?? "");
        setPostcode(data?.postcode ?? "");
      });
  }, [activeBusinessId]);

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    if (!activeBusinessId) return;
    setError(null);
    setSaved(false);
    const { error: err } = await supabase.rpc("rpc_portal_save_business_profile", {
      p_business_id: activeBusinessId,
      p_address_line: addressLine,
      p_suburb: suburb,
      p_state: state,
      p_postcode: postcode,
    });
    if (err) {
      setError(err.message);
      return;
    }
    setSaved(true);
  }

  const touch = <T,>(set: (v: T) => void) => (v: T) => {
    setSaved(false);
    set(v);
  };

  return (
    <SectionCard title="Address" description="Where callers can find you.">
      <form onSubmit={handleSave} style={{ display: "flex", flexDirection: "column", gap: 10, maxWidth: 520 }}>
        <Field label="Street address">
          <Input value={addressLine} maxLength={200} onChange={(e) => touch(setAddressLine)(e.target.value)} placeholder="12 High Street" />
        </Field>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <Field label="Suburb">
            <Input value={suburb} maxLength={100} onChange={(e) => touch(setSuburb)(e.target.value)} style={{ width: 200 }} />
          </Field>
          <Field label="State">
            <div style={{ width: 230, fontWeight: 400, color: "var(--rc-text)" }}>
              {state || "Not set"}
              {timeZone && <div style={{ fontSize: 12.5, color: "var(--rc-text-muted)" }}>{timeZoneLabel(timeZone)}</div>}
              <div style={{ fontSize: 12, color: "var(--rc-text-faint)" }}>Set by your account manager.</div>
            </div>
          </Field>
          <Field label="Postcode">
            <Input value={postcode} maxLength={10} inputMode="numeric" onChange={(e) => touch(setPostcode)(e.target.value)} style={{ width: 100 }} />
          </Field>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Button type="submit" variant="primary">
            Save address
          </Button>
          {saved && <span style={{ fontSize: 12.5, color: "var(--rc-success)" }}>Saved.</span>}
        </div>
      </form>
      {error && <p style={errorStyle}>{error}</p>}
    </SectionCard>
  );
}
